import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 1346c (Chad: "still 0:00") — THE frozen-clock root cause: the API's DateTimes round-trip
// SQL Server as Kind=Unspecified and serialize WITHOUT a timezone suffix; the browser then
// parses them as LOCAL time (7h in the future for PT), elapsed went negative, and the clamp
// pinned every clock at 0:00. Treat suffix-less server timestamps as UTC, always.
export const parseUtcMs = (iso?: string | null): number => {
  if (!iso) return NaN;
  return new Date(/[zZ]$|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`).getTime();
};

// item 1337 — the personal start/stop timer that BOOKS INTO WORK ORDERS (never timesheets).
export interface TimeClockDay { date: string; seconds: number; hours: number }
export interface TimeClockRow {
  id: string;
  note?: string | null;
  startedAt: string;
  stoppedAt?: string | null;
  // item 1338b/1339 — segmented time: banked seconds + the live segment (null = idle)
  accumulatedSeconds: number;
  segmentStartedAt?: string | null;
  paused: boolean;
  roundedHours: number;
  // item 1339 — the per-day picture booking will mint (one WO line per Pacific day)
  days: TimeClockDay[];
}
export interface TimeClockMine {
  running: TimeClockRow | null;
  unbooked: TimeClockRow[];
}

const useTimeClock = () => {
  const queryClient = useQueryClient();
  const invalidate = async () => {
    await queryClient.invalidateQueries('timeClock');
    await queryClient.invalidateQueries('timeClockHistory'); // item 1337b — the MY TIME page
  };
  const query = useQuery<TimeClockMine>('timeClock', async () => {
    const { data } = await axios.get(`${apiUrl()}TimeClock/mine`);
    return data;
  }, { refetchInterval: 60000 });

  const startMutation = useMutation(
    (note: string) => axios.post(`${apiUrl()}TimeClock/start`, { note }),
    { onSettled: invalidate },
  );
  const stopMutation = useMutation(
    (id: string) => axios.post(`${apiUrl()}TimeClock/${id}/stop`, null),
    { onSettled: invalidate },
  );
  // item 1338b — pause a running clock (or un-stop back to paused) / resume the segment
  const pauseMutation = useMutation(
    (id: string) => axios.post(`${apiUrl()}TimeClock/${id}/pause`, null),
    { onSettled: invalidate },
  );
  const resumeMutation = useMutation(
    (id: string) => axios.post(`${apiUrl()}TimeClock/${id}/resume`, null),
    { onSettled: invalidate },
  );
  const bookMutation = useMutation(
    (vars: { id: string; workOrderId: string; itemId: string; note?: string; days?: { date: string; hours: number }[] }) => axios
      .post(`${apiUrl()}TimeClock/${vars.id}/book`, {
        workOrderId: vars.workOrderId, itemId: vars.itemId, note: vars.note, days: vars.days,
      }),
    {
      onSettled: async () => {
        await invalidate();
        // the booked line changed the work order — refresh anything showing it
        await queryClient.invalidateQueries('workOrders');
        await queryClient.invalidateQueries('workOrder');
        await queryClient.invalidateQueries('project');
      },
    },
  );
  const discardMutation = useMutation(
    (id: string) => axios.post(`${apiUrl()}TimeClock/${id}/discard`, null),
    { onSettled: invalidate },
  );
  // item 1338 — rebook (only after the WO line was deleted; the server verifies) + note edit
  const rebookMutation = useMutation(
    (id: string) => axios.post(`${apiUrl()}TimeClock/${id}/rebook`, null),
    { onSettled: invalidate },
  );
  const noteMutation = useMutation(
    (vars: { id: string; note: string }) => axios.post(`${apiUrl()}TimeClock/${vars.id}/note`, { note: vars.note }),
    { onSettled: invalidate },
  );

  return {
    mine: query.data,
    status: query.status,
    start: (note: string) => startMutation.mutateAsync(note),
    stop: (id: string) => stopMutation.mutateAsync(id),
    pause: (id: string) => pauseMutation.mutateAsync(id),
    resume: (id: string) => resumeMutation.mutateAsync(id),
    book: (id: string, workOrderId: string, itemId: string, note?: string, days?: { date: string; hours: number }[]) => bookMutation.mutateAsync({
      id, workOrderId, itemId, note, days,
    }),
    discard: (id: string) => discardMutation.mutateAsync(id),
    rebook: (id: string) => rebookMutation.mutateAsync(id),
    saveNote: (id: string, note: string) => noteMutation.mutateAsync({ id, note }),
    isBusy: startMutation.isLoading || stopMutation.isLoading || bookMutation.isLoading || discardMutation.isLoading,
  };
};

export default useTimeClock;
